import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { MapPin, ShoppingBag, Download, ExternalLink, ArrowRight } from 'lucide-react'
import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.distrihub.co.ke/api/v1'
const APK_URL = 'https://expo.dev/artifacts/eas/_nlrOcroPMsLOdTMzfmYk_3yBza9aYSv_LNSRfOTHDE.apk'

interface CatalogItem {
  listing_id: string
  name: string
  price: string
  image: string | null
  description: string | null
  neighbourhood: string | null
  is_open_now: boolean
}

interface BranchData {
  business: string
  business_id: string
  branch: string
  branch_id: string
  cover_image: string | null
  catalog: CatalogItem[]
  count: number
}

export const Route = createFileRoute('/s/$branchId')({
  component: StoreLinkPage,
})

function StoreLinkPage() {
  const { branchId } = Route.useParams()
  const [data, setData] = useState<BranchData | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    axios
      .get<BranchData>(`${API_BASE}/distrimarket/branch/${branchId}/`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
    axios.post(`${API_BASE}/distrimarket/store-scan/${branchId}/`, { source: 'link' }).catch(() => {})
  }, [branchId])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4 text-center gap-4">
        <ShoppingBag size={40} className="text-gray-300" />
        <h1 className="text-xl font-bold text-gray-800">Store not found</h1>
        <p className="text-gray-500 text-sm">This store link may have expired or been removed.</p>
        <Link to="/" className="mt-2 text-sm text-rose-600 font-medium hover:underline flex items-center gap-1">
          Go to DistriHub <ArrowRight size={14} />
        </Link>
      </div>
    )
  }

  const storeDeepLink = `distrimarket:///store/${branchId}`

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Cover / hero */}
      {data?.cover_image ? (
        <div className="h-44 w-full bg-gray-200 overflow-hidden">
          <img src={data.cover_image} alt={data?.business} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="h-28 w-full bg-gradient-to-br from-rose-600 to-rose-800" />
      )}

      {/* Store header card */}
      <div className="mx-4 -mt-6 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
          <ShoppingBag size={20} className="text-rose-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-base text-gray-900 leading-tight truncate">{data?.business}</h1>
          {data?.branch && data.branch !== data.business && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{data.branch}</p>
          )}
          {data?.catalog[0]?.neighbourhood && (
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={10} className="text-rose-400 shrink-0" />
              <span className="text-xs text-gray-400">{data.catalog[0].neighbourhood}</span>
            </div>
          )}
        </div>
        <a
          href={storeDeepLink}
          className="shrink-0 flex items-center gap-1.5 bg-rose-600 text-white text-xs font-semibold px-3 py-2 rounded-xl hover:bg-rose-700 transition-colors"
        >
          <ExternalLink size={12} />
          Open
        </a>
      </div>

      {/* Product catalog */}
      <div className="mx-4 mt-4">
        {data && data.catalog.length > 0 ? (
          <>
            <p className="text-xs text-gray-400 mb-3">{data.count} product{data.count !== 1 ? 's' : ''}</p>
            <div className="grid grid-cols-2 gap-3">
              {data.catalog.map((item) => (
                <a
                  key={item.listing_id}
                  href={`/p/${item.listing_id}`}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow block"
                >
                  <div className="h-36 bg-gray-100 overflow-hidden">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">📦</div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <p className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2">{item.name}</p>
                    <p className="text-sm font-bold text-rose-600 mt-1">
                      KES {Number(item.price).toLocaleString('en-KE', { maximumFractionDigits: 0 })}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
            <ShoppingBag size={32} className="text-gray-200" />
            <p className="text-sm text-gray-400">No products listed yet</p>
          </div>
        )}
      </div>

      {/* Sticky bottom CTAs */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-100 px-4 py-3 flex gap-2 max-w-lg mx-auto">
        <a
          href={storeDeepLink}
          className="flex-1 flex items-center justify-center gap-2 bg-rose-600 text-white font-semibold text-sm py-3 rounded-xl hover:bg-rose-700 transition-colors"
        >
          <ExternalLink size={15} />
          Order on DistriMarket
        </a>
        <a
          href={APK_URL}
          className="flex items-center justify-center gap-1.5 border border-gray-200 text-gray-700 font-medium text-sm px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
        >
          <Download size={15} />
          <span className="text-xs">Get App</span>
        </a>
      </div>

      {/* Powered by */}
      <div className="mt-8 flex items-center justify-center gap-1.5 text-xs text-gray-400">
        Powered by Distri<span className="text-rose-600 font-semibold">Hub</span>
      </div>
    </div>
  )
}
